// Grille tarifaire Colissimo Domicile au départ des DOM, vers les DOM (intra-DOM).
// Source : https://www.colissimo.entreprise.laposte.fr/offres-et-services/tarifs-generaux/tarifs-colissimo-domicile-au-depart-des-dom
// Dernière mise à jour : 2026-05-15.
// Quand La Poste met à jour ses tarifs (typiquement 1×/an), mettre à jour ce fichier.
//
// Chaque tranche : weightMaxKg est exclusif côté haut (un colis pile à 1.00kg
// est facturé dans la tranche 0.75-1.00, pas 1.00-2.00 — cf. arrondi La Poste).
// Pour rester safe, le lookup utilise `weight <= weightMaxKg`.
const INTRA_DOM_RATES = [
  { maxKg: 0.25, priceNoSig: 6.84,  priceWithSig: 7.89  },
  { maxKg: 0.50, priceNoSig: 7.71,  priceWithSig: 8.76  },
  { maxKg: 0.75, priceNoSig: 8.60,  priceWithSig: 9.65  },
  { maxKg: 1.00, priceNoSig: 9.34,  priceWithSig: 10.39 },
  { maxKg: 2.00, priceNoSig: 10.48, priceWithSig: 11.53 },
  { maxKg: 3.00, priceNoSig: 11.49, priceWithSig: 12.54 },
  { maxKg: 4.00, priceNoSig: 12.54, priceWithSig: 13.59 },
  { maxKg: 5.00, priceNoSig: 13.54, priceWithSig: 14.59 },
  { maxKg: 6.00, priceNoSig: 14.17, priceWithSig: 15.22 },
  { maxKg: 7.00, priceNoSig: 15.16, priceWithSig: 16.21 },
  { maxKg: 8.00, priceNoSig: 16.15, priceWithSig: 17.20 },
  { maxKg: 9.00, priceNoSig: 17.17, priceWithSig: 18.22 },
  { maxKg: 10.00, priceNoSig: 18.17, priceWithSig: 19.20 },
  { maxKg: 11.00, priceNoSig: 18.79, priceWithSig: 19.84 },
  { maxKg: 12.00, priceNoSig: 19.77, priceWithSig: 20.82 },
  { maxKg: 13.00, priceNoSig: 20.74, priceWithSig: 21.79 },
  { maxKg: 14.00, priceNoSig: 21.75, priceWithSig: 22.80 },
  { maxKg: 15.00, priceNoSig: 22.73, priceWithSig: 23.78 },
  { maxKg: 16.00, priceNoSig: 23.70, priceWithSig: 24.75 },
  { maxKg: 17.00, priceNoSig: 24.68, priceWithSig: 25.73 },
  { maxKg: 18.00, priceNoSig: 25.66, priceWithSig: 26.71 },
  { maxKg: 19.00, priceNoSig: 26.65, priceWithSig: 27.70 },
  { maxKg: 20.00, priceNoSig: 27.62, priceWithSig: 28.67 },
  { maxKg: 21.00, priceNoSig: 28.33, priceWithSig: 29.38 },
  { maxKg: 22.00, priceNoSig: 29.29, priceWithSig: 30.34 },
  { maxKg: 23.00, priceNoSig: 30.28, priceWithSig: 31.33 },
  { maxKg: 24.00, priceNoSig: 31.25, priceWithSig: 32.30 },
  { maxKg: 25.00, priceNoSig: 32.19, priceWithSig: 33.24 },
  { maxKg: 26.00, priceNoSig: 33.19, priceWithSig: 34.24 },
  { maxKg: 27.00, priceNoSig: 34.13, priceWithSig: 35.18 },
  { maxKg: 28.00, priceNoSig: 35.11, priceWithSig: 36.16 },
  { maxKg: 29.00, priceNoSig: 36.12, priceWithSig: 37.17 },
  { maxKg: 30.00, priceNoSig: 37.05, priceWithSig: 38.10 },
];

export const COLISSIMO_MAX_WEIGHT_KG = 30;

// Détecte la zone de livraison à partir d'un code postal français.
// Retourne 'intra-dom' si destination en DOM (Antilles-Guyane-Réunion-Mayotte),
// 'unsupported' sinon (métropole ou autre — pas géré par cette intégration).
export function detectShippingZone(postalCode) {
  if (!postalCode) return 'unsupported';
  const code = String(postalCode).trim();
  // DOM = codes postaux à 5 chiffres commençant par 97 ou 98
  if (/^97\d{3}$/.test(code) || /^98\d{3}$/.test(code)) {
    return 'intra-dom';
  }
  return 'unsupported';
}

// Renvoie le tarif Colissimo correspondant pour un poids donné.
// weightKg: nombre (en kilogrammes, ex: 1.5)
// zone: 'intra-dom'
// withSignature: boolean
// Retourne { price: number, bracket: {maxKg} } ou null si hors barème.
export function getColissimoRate(weightKg, zone, withSignature = false) {
  if (zone !== 'intra-dom') return null;
  if (weightKg == null || isNaN(weightKg) || weightKg <= 0) return null;
  if (weightKg > COLISSIMO_MAX_WEIGHT_KG) return null;

  const bracket = INTRA_DOM_RATES.find((b) => weightKg <= b.maxKg);
  if (!bracket) return null;
  return {
    price: withSignature ? bracket.priceWithSig : bracket.priceNoSig,
    bracket,
  };
}

// Calcule le poids total d'un panier de vente.
// cart: tableau d'items {product: {weight}, quantity}
// Retourne { totalKg, hasAllWeights, missingProductTitles, weightedCount }
// - totalKg : somme des poids des produits qui ont un poids renseigné
// - hasAllWeights : true si TOUS les produits ont un poids
// - missingProductTitles : titres des produits sans poids
// - weightedCount : nombre de produits qui ont contribué au totalKg
// Colissimo est éligible dès qu'au moins UN produit a un poids (weightedCount > 0)
// — les produits sans poids sont ignorés du calcul, la cliente complète au comptoir.
export function calculateCartWeight(cart) {
  let totalKg = 0;
  let weightedCount = 0;
  let hasAllWeights = true;
  const missingProductTitles = [];

  for (const item of cart) {
    const weight = item.product?.weight;
    const quantity = item.quantity || 1;
    if (weight == null || weight === '' || isNaN(parseFloat(weight))) {
      hasAllWeights = false;
      if (item.product?.title) missingProductTitles.push(item.product.title);
    } else {
      totalKg += parseFloat(weight) * quantity;
      weightedCount += 1;
    }
  }

  return { totalKg, hasAllWeights, missingProductTitles, weightedCount };
}
